import dayjs from 'dayjs';
import React, { JSX } from 'react';
import Flickity from 'react-flickity-component';
import { classNames } from '@/helpers/common';
import EventSiteHeader from '@/model/EventSiteHeader';

interface StateProps {
  eventDates: EventSiteHeader[];
  activeId?: string;
}
interface DispatchProps {
  handleOnClick: (eventDate: EventSiteHeader) => void;
}

interface TicketProps {
  item: EventSiteHeader;
  isActive: boolean;
  handleOnClick: (item: EventSiteHeader) => void;
}

type Props = StateProps & DispatchProps;

const TicketPassport: React.FC<TicketProps> = ({ item, isActive, handleOnClick }) => {
  const ref = React.useRef<HTMLAnchorElement>(null);

  React.useEffect(() => {
    const applyClasses = (): void => {
      if (ref.current) {
        if (isActive) {
          ref.current.classList.add(
            'bg-alert-info-soft',
            'text-white',
            'font-bold',
            'border-alert-info-soft',
          );
          ref.current.classList.remove('bg-neutro-w-100', 'text-neutro-w-600', 'font-normal');

          const badge = ref.current.querySelector('.slider-date-badge');
          if (badge) {
            badge.classList.add('bg-neutro-w-000', 'text-alert-info-soft');
            badge.classList.remove('bg-neutro-w-400', 'text-neutro-w-000');
          }

          const dateText = ref.current.querySelector('.slider-date-text');
          if (dateText) {
            dateText.classList.add('text-white');
            dateText.classList.remove('text-neutro-w-400');
          }
        } else {
          ref.current.classList.add('bg-neutro-w-100', 'text-neutro-w-600', 'font-normal');
          ref.current.classList.remove(
            'bg-alert-info-soft',
            'text-white',
            'font-bold',
            'border-alert-info-soft',
          );

          const badge = ref.current.querySelector('.slider-date-badge');
          if (badge) {
            badge.classList.add('bg-neutro-w-400', 'text-neutro-w-000');
            badge.classList.remove('bg-neutro-w-000', 'text-alert-info-soft');
          }

          const dateText = ref.current.querySelector('.slider-date-text');
          if (dateText) {
            dateText.classList.add('text-neutro-w-400');
            dateText.classList.remove('text-white');
          }
        }
      }
    };

    applyClasses();
    const timeoutId = setTimeout(applyClasses, 100);
    return () => clearTimeout(timeoutId);
  }, [isActive]);

  return (
    <a
      ref={ref}
      key={item.id}
      onClick={() => handleOnClick(item)}
      className={classNames(
        isActive
          ? 'bg-alert-info-soft text-white font-bold border-alert-info-soft'
          : 'bg-neutro-w-100 text-neutro-w-600 font-normal border-neutro-w-100',
        'carousel-cell w-[135px] mr-5 cursor-pointer p-2 flex flex-col justify-center items-center border hover:border-alert-info-soft',
      )}
    >
      {item.count > 0 && (
        <div
          className={classNames(
            'slider-date-badge w-[14px] h-[14px] rounded-full font-dmsans font-normal text-[10px] flex justify-center items-center absolute top-[5px] right-[5px]',
            isActive ? 'bg-neutro-w-000 text-alert-info-soft' : 'bg-neutro-w-400 text-neutro-w-000',
          )}
        >
          {item.count}
        </div>
      )}
      {item.passportName && item.passportName.trim().length > 0 ? (
        <span className="text-center">{item.passportName}</span>
      ) : (
        <>{item.name && <span className="text-center">{item.name}</span>}</>
      )}

      <span
        className={classNames(
          'slider-date-text font-normal text-center text-xs',
          isActive ? 'text-white' : 'text-neutro-w-400',
        )}
      >
        {`${dayjs(item.startDate).format('DD/MM')} até ${dayjs(item.endDate).format('DD/MM')}`}
      </span>
    </a>
  );
};

const TicketUnit: React.FC<TicketProps> = ({ item, isActive, handleOnClick }) => {
  const ref = React.useRef<HTMLAnchorElement>(null);

  React.useEffect(() => {
    const applyClasses = (): void => {
      if (ref.current) {
        if (isActive) {
          ref.current.classList.add(
            'bg-alert-info-soft',
            'text-white',
            'font-bold',
            'border-alert-info-soft',
          );
          ref.current.classList.remove(
            'bg-neutro-w-100',
            'text-neutro-w-600',
            'font-normal',
            'border-neutro-w-100',
          );

          const badge = ref.current.querySelector('.slider-date-badge');
          if (badge) {
            badge.classList.add('bg-neutro-w-000', 'text-alert-info-soft');
            badge.classList.remove('bg-neutro-w-400', 'text-neutro-w-000');
          }
        } else {
          ref.current.classList.add(
            'bg-neutro-w-100',
            'text-neutro-w-600',
            'font-normal',
            'border-neutro-w-100',
          );
          ref.current.classList.remove(
            'bg-alert-info-soft',
            'text-white',
            'font-bold',
            'border-alert-info-soft',
          );

          const badge = ref.current.querySelector('.slider-date-badge');
          if (badge) {
            badge.classList.add('bg-neutro-w-400', 'text-neutro-w-000');
            badge.classList.remove('bg-neutro-w-000', 'text-alert-info-soft');
          }
        }
      }
    };

    applyClasses();
    const timeoutId = setTimeout(applyClasses, 100);
    return () => clearTimeout(timeoutId);
  }, [isActive]);

  return (
    <a
      ref={ref}
      key={item.id}
      onClick={() => handleOnClick(item)}
      className={classNames(
        isActive
          ? 'bg-alert-info-soft text-white font-bold border-alert-info-soft'
          : 'bg-neutro-w-100 text-neutro-w-600 font-normal border-neutro-w-100',
        'relative carousel-cell w-[59px] mr-5 cursor-pointer p-[14px] flex flex-col justify-center items-center border hover:border-alert-info-soft',
      )}
    >
      {item.count > 0 && (
        <div
          className={classNames(
            'slider-date-badge w-[14px] h-[14px] rounded-full font-dmsans font-normal text-[10px] flex justify-center items-center absolute top-[5px] right-[5px]',
            isActive ? 'bg-neutro-w-000 text-alert-info-soft' : 'bg-neutro-w-400 text-neutro-w-000',
          )}
        >
          {item.count}
        </div>
      )}

      <div className="m-0 font-dmsans leading-[21px]">{`${dayjs(item.startDate).format('DD')}`}</div>
      <div className="m-0 font-dmsans leading-[21px] uppercase">{`${dayjs(item.startDate)
        .locale('pt-br')
        .format('MMM')}`}</div>

      <div className="font-dmsans font-normal text-xs">{`${dayjs(item.startDate).format(
        'HH:mm',
      )}`}</div>
      <div className="font-dmsans font-normal text-xs">hrs</div>
    </a>
  );
};

const SliderDate: React.FC<Props> = (props: Props): JSX.Element => {
  const flickityOptions = {
    freeScroll: true,
    percentPosition: true,
    cellAlign: 'left',
  };
  return (
    <Flickity options={flickityOptions} className={'slider-ticket-product pl-[60px] mt-6'}>
      {props.eventDates.map(data => {
        const isActive =
          props.eventDates.length === 1
            ? true
            : String(data.id) === String(props.activeId) ||
              String(data.eventDate?.id) === String(props.activeId);
        return data.isPassport ? (
          <TicketPassport
            key={data.id}
            item={data}
            isActive={isActive}
            handleOnClick={props.handleOnClick}
          />
        ) : (
          <TicketUnit
            key={data.id}
            item={data}
            isActive={isActive}
            handleOnClick={props.handleOnClick}
          />
        );
      })}
    </Flickity>
  );
};

export default SliderDate;
